import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type { KundeListe as KundeListeEintrag, Unternehmen } from "@kundeninfo/api-client";
import { Icon } from "@kundeninfo/ui";
import { markenTextfarben } from "../markenfarben.js";
import { relativeZeit } from "../format.js";
import "./KundenListe.css";

/** Schmales Fehlerbild — passt sowohl eine ApiError-Instanz als auch ein Test-Double. */
export type KundenFehler = { message: string; code?: string; incidentId?: string };

export type KundenListeProps = {
  items: KundeListeEintrag[];
  loading: boolean;
  error: KundenFehler | null;
  search: string;
  onSearch: (value: string) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Für die Kürzel-Chips in Markenfarbe. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
};

const SUCH_VERZOEGERUNG_MS = 250;

function chipStil(colorHex: string | undefined): CSSProperties | undefined {
  if (colorHex === undefined) return undefined;
  const { hell, dunkel } = markenTextfarben(colorHex);
  return {
    "--kl-chip-bg": `color-mix(in srgb, ${colorHex} 14%, var(--ki-surface))`,
    "--kl-chip-rand": `color-mix(in srgb, ${colorHex} 40%, transparent)`,
    "--kl-chip-text-hell": hell,
    "--kl-chip-text-dunkel": dunkel,
  } as CSSProperties;
}

export function KundenListe(p: KundenListeProps): ReactElement {
  const companies = p.companies ?? [];
  const [eingabe, setEingabe] = useState(p.search);
  const letztGemeldet = useRef(p.search);

  // Ändert sich die Suche von außen (z. B. weil sie zurückgesetzt wurde), zieht das Feld nach.
  useEffect(() => {
    setEingabe(p.search);
    letztGemeldet.current = p.search;
  }, [p.search]);

  useEffect(() => {
    if (eingabe === letztGemeldet.current) return;
    const timer = setTimeout(() => {
      letztGemeldet.current = eingabe;
      p.onSearch(eingabe);
    }, SUCH_VERZOEGERUNG_MS);
    return () => clearTimeout(timer);
    // p.onSearch bewusst nicht in den Abhängigkeiten: Ein neuer Funktionswert bei jedem
    // Elternrender darf den laufenden Verzögerungs-Timer nicht neu starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingabe]);

  return (
    <div className="kl">
      <div className="kl__suche">
        <input
          id="kl-suchfeld"
          type="search"
          className="kl__suchfeld"
          aria-label="Kunden durchsuchen"
          placeholder="Firma oder Ort suchen"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
        />
      </div>

      {p.error !== null && (
        <div className="kl__fehler" role="alert">
          <Icon name="triangle-exclamation" size={16} label="Fehler" />
          <span>{p.error.message}</span>
        </div>
      )}

      {/* Gegenstück zu role="alert" oben: role="status" kündigt sich Screenreadern von
          selbst an (implizites aria-live="polite"). p.loading kommt ausschließlich aus der
          echten Ladeoperation in App.tsx (dort erst nach der 250-ms-Verzögerung dieser
          Komponente gesetzt) — der Hinweis flackert deshalb nicht bei jedem Tastendruck. */}
      {p.error === null && p.loading && (
        <p className="kl__lade" role="status">
          <span className="kl__lade-spinner" aria-hidden="true" />
          <span>Lädt …</span>
        </p>
      )}

      {p.error === null && !p.loading && p.items.length === 0 && (
        <p className="kl__hinweis">Kein Kunde gefunden.</p>
      )}

      <ul className="kl__treffer">
        {p.items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={`kl__eintrag${item.id === p.selectedId ? " kl__eintrag--aktiv" : ""}`}
              aria-current={item.id === p.selectedId ? "true" : undefined}
              onClick={() => p.onSelect(item.id)}
            >
              <span className="kl__firma">{item.companyName}</span>
              <span className="kl__zeile2">
                {item.city !== null && item.city !== undefined && item.city !== "" && (
                  <span className="kl__ort">{item.city}</span>
                )}
                <span className="kl__chips">
                  {item.relations.map((r) => (
                    <span
                      key={r.shortCode}
                      className="kl__chip"
                      style={chipStil(companies.find((c) => c.shortCode === r.shortCode)?.colorHex)}
                    >
                      {r.shortCode}
                    </span>
                  ))}
                </span>
                <span className="kl__aktivitaet">{relativeZeit(item.lastActivityAt)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
